import {
  type CalendarEvent,
  CalendarView,
} from '@agentic-flow-studio/shared/components/calendar/CalendarView';
import { CalendarViewSwitcher } from '@agentic-flow-studio/shared/components/calendar/CalendarViewSwitcher';
import { formatOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { Job } from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import type { CalendarEventExternal } from '@schedule-x/calendar';
import {
  Briefcase,
  Calendar,
  CalendarDays,
  Clock,
  ExternalLink,
  Filter,
  Loader2,
  MapPin,
  Plus,
  Route,
  Truck,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Temporal } from 'temporal-polyfill';

export default function CalendarPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const todayIso = new Date().toISOString().split('T')[0];
  const [selectedDateIso, setSelectedDateIso] = useState<string>(todayIso);
  const [currentView, setCurrentView] = useState<'day' | 'week' | 'month-grid' | 'list'>(
    'month-grid'
  );
  const [operatorFilter, setOperatorFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [reschedulingJob, setReschedulingJob] = useState<Job | null>(null);
  const [targetRescheduleDate, setTargetRescheduleDate] = useState<string>('');

  const { data: allJobs = [], isLoading: isJobsLoading } = trpc.jobPlan.jobs.list.useQuery({});
  const { data: operators = [] } = trpc.jobPlan.operators.list.useQuery({ activeOnly: true });

  const rescheduleMutation = trpc.jobPlan.jobs.reschedule.useMutation({
    onSuccess: () => {
      toast.success('Job rescheduled successfully');
      setReschedulingJob(null);
      utils.jobPlan.jobs.list.invalidate();
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  // Filter jobs based on selected operator and status
  const filteredJobs = useMemo(() => {
    return allJobs.filter((job) => {
      if (statusFilter !== 'all' && job.status !== statusFilter) return false;
      if (operatorFilter !== 'all') {
        const opId = parseInt(operatorFilter, 10);
        if (job.assignedOperator?.operatorProfileId !== opId) return false;
      }
      return true;
    });
  }, [allJobs, operatorFilter, statusFilter]);

  // Jobs on the currently focused date
  const jobsOnSelectedDate = useMemo(() => {
    return filteredJobs.filter((j) => j.serviceDate === selectedDateIso);
  }, [filteredJobs, selectedDateIso]);

  // Convert jobs to CalendarEvents for Schedule-X
  const events: CalendarEvent[] = useMemo(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    return filteredJobs.map((job) => {
      let start: Temporal.PlainDate | Temporal.ZonedDateTime;
      let end: Temporal.PlainDate | Temporal.ZonedDateTime;

      if (job.windowStartOffsetSeconds != null) {
        const startSec = Math.max(0, Math.min(86340, job.windowStartOffsetSeconds));
        const duration = job.estimatedDurationSeconds || 3600;
        const endSec = Math.max(startSec + 60, Math.min(86399, startSec + duration));

        const startTimeStr = formatOffsetTime(startSec);
        const endTimeStr = formatOffsetTime(endSec);

        try {
          start = Temporal.PlainDateTime.from(
            `${job.serviceDate}T${startTimeStr}:00`
          ).toZonedDateTime(timeZone);
          end = Temporal.PlainDateTime.from(`${job.serviceDate}T${endTimeStr}:00`).toZonedDateTime(
            timeZone
          );
        } catch {
          start = Temporal.PlainDate.from(job.serviceDate);
          end = Temporal.PlainDate.from(job.serviceDate);
        }
      } else {
        start = Temporal.PlainDate.from(job.serviceDate);
        end = Temporal.PlainDate.from(job.serviceDate);
      }

      return {
        id: job.id,
        title: job.title,
        start,
        end,
        assignedUser: job.assignedOperator?.operatorName
          ? {
              id: job.assignedOperator.operatorProfileId,
              name: job.assignedOperator.operatorName,
              email: job.assignedOperator.operatorEmail || '',
              avatarUrl: null,
            }
          : null,
      };
    });
  }, [filteredJobs]);

  const handleEventClick = (event: CalendarEventExternal) => {
    const jobId = typeof event.id === 'number' ? event.id : parseInt(String(event.id), 10);
    const found = allJobs.find((j) => j.id === jobId);
    if (found) {
      setSelectedJob(found);
      setSelectedDateIso(found.serviceDate);
    }
  };

  const handleDateSelect = (date: Temporal.PlainDate) => {
    setSelectedDateIso(date.toString());
  };

  const handleExecuteReschedule = () => {
    if (!reschedulingJob || !targetRescheduleDate) return;
    rescheduleMutation.mutate({
      jobId: reschedulingJob.id,
      newServiceDate: targetRescheduleDate,
    });
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden font-sans">
      {/* ── Top Header ─────────────────────────────────────────────── */}
      <div className="p-4 border-b border-border/40 bg-card flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
            <Calendar className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-base font-bold text-foreground tracking-tight">
              Job Plan Multi-Day Calendar
            </h1>
            <p className="text-xs text-muted-foreground">
              Cross-day fleet workload, unassigned jobs, and scheduling overview
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Operator Filter */}
          <Select value={operatorFilter} onValueChange={setOperatorFilter}>
            <SelectTrigger className="h-8 text-xs w-44 bg-background">
              <Users className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="All Operators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Operators</SelectItem>
              {operators.map((op) => (
                <SelectItem key={op.id} value={String(op.id)}>
                  {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs w-36 bg-background">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>

          {/* View Mode Switcher */}
          <CalendarViewSwitcher value={currentView} onChange={setCurrentView} />

          <Button
            size="sm"
            asChild
            className="h-8 text-xs gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link to={`/org/${orgId}/workspace/${workspaceId}/jobs/new`}>
              <Plus className="h-3.5 w-3.5" />
              <span>New Job</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* ── Main Body: Calendar + Day Inspector ────────────────────── */}
      <div className="flex-1 flex overflow-hidden p-4 gap-4 bg-muted/20">
        {/* Left / Center: Interactive Calendar Grid */}
        <div className="flex-1 min-w-0 bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden flex flex-col">
          {isJobsLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <CalendarView
              key={`${currentView}-${selectedDateIso}`}
              events={events}
              defaultView={currentView}
              selectedDate={Temporal.PlainDate.from(selectedDateIso)}
              onSelectedDateUpdate={handleDateSelect}
              onEventClick={handleEventClick}
              className="border-0 shadow-none rounded-none"
            />
          )}
        </div>

        {/* Right: Focused Day Inspector & Job Plan Launcher */}
        <div className="w-84 flex-shrink-0 flex flex-col bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden">
          {/* Day Header */}
          <div className="p-3.5 border-b border-border/40 bg-muted/30 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" />
                <span className="font-semibold text-xs text-foreground">
                  {new Date(`${selectedDateIso}T00:00:00`).toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <Badge variant="secondary" className="text-[10px] font-mono">
                {jobsOnSelectedDate.length} Job{jobsOnSelectedDate.length !== 1 ? 's' : ''}
              </Badge>
            </div>

            <Button
              size="sm"
              className="w-full h-8 text-xs gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
              onClick={() =>
                navigate(`/org/${orgId}/workspace/${workspaceId}/schedule?date=${selectedDateIso}`)
              }
            >
              <Route className="h-3.5 w-3.5" />
              <span>Open Day on Schedule Board</span>
            </Button>
          </div>

          {/* Job Cards on Selected Day */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {jobsOnSelectedDate.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-center p-4 text-muted-foreground space-y-1">
                <Briefcase className="h-6 w-6 text-muted-foreground/40" />
                <p className="text-xs font-medium text-foreground">No jobs scheduled</p>
                <p className="text-[11px]">Click a calendar date or schedule jobs for this day.</p>
              </div>
            ) : (
              jobsOnSelectedDate.map((job) => (
                <div
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className="p-2.5 rounded-xl border border-border/60 bg-card hover:border-primary/50 transition-all cursor-pointer space-y-1.5 shadow-2xs"
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-xs text-foreground truncate flex-1">
                      {job.title}
                    </span>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 capitalize shrink-0">
                      {job.status.replace('_', ' ')}
                    </Badge>
                  </div>

                  <p className="text-[11px] text-muted-foreground truncate">
                    {job.formattedAddress || 'No address'}
                  </p>

                  <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-border/30">
                    <div className="flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      <span>
                        {job.windowStartOffsetSeconds != null
                          ? formatOffsetTime(job.windowStartOffsetSeconds)
                          : 'Flexible'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      {job.assignedOperator ? (
                        <span className="text-foreground font-medium flex items-center gap-1">
                          <Truck className="h-2.5 w-2.5 text-primary" />
                          <span className="truncate max-w-[90px]">
                            {job.assignedOperator.operatorName}
                          </span>
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                          Unassigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Bottom Quick Stats */}
          <div className="p-3 border-t border-border/40 bg-muted/20 text-[11px] text-muted-foreground flex items-center justify-between">
            <span>{jobsOnSelectedDate.filter((j) => !j.assignedOperator).length} unassigned</span>
            <span>
              {jobsOnSelectedDate.filter((j) => j.status === 'completed').length} completed
            </span>
          </div>
        </div>
      </div>

      {/* ── Job Quick Preview Dialog ───────────────────────────────── */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-md p-4">
          {selectedJob && (
            <div className="space-y-3.5">
              <DialogHeader>
                <div className="flex items-center justify-between gap-2">
                  <DialogTitle className="text-base font-bold text-foreground">
                    {selectedJob.title}
                  </DialogTitle>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {selectedJob.status.replace('_', ' ')}
                  </Badge>
                </div>
                <DialogDescription className="text-xs text-muted-foreground">
                  Service Date: {selectedJob.serviceDate}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2 p-2.5 rounded-lg border border-border/50 bg-muted/20">
                  <MapPin className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground">{selectedJob.formattedAddress}</span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-lg border border-border/40 bg-card">
                    <span className="text-[10px] text-muted-foreground block">Duration</span>
                    <span className="font-semibold text-foreground">
                      {Math.round(selectedJob.estimatedDurationSeconds / 60)} mins
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg border border-border/40 bg-card">
                    <span className="text-[10px] text-muted-foreground block">Arrival Window</span>
                    <span className="font-semibold text-foreground">
                      {selectedJob.windowStartOffsetSeconds != null &&
                      selectedJob.windowEndOffsetSeconds != null
                        ? `${formatOffsetTime(selectedJob.windowStartOffsetSeconds)} – ${formatOffsetTime(selectedJob.windowEndOffsetSeconds)}`
                        : 'Flexible'}
                    </span>
                  </div>
                </div>

                {selectedJob.assignedOperator && (
                  <div className="p-2.5 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-muted-foreground block">
                        Assigned Operator
                      </span>
                      <span className="font-semibold text-foreground">
                        {selectedJob.assignedOperator.operatorName}
                      </span>
                    </div>
                    <Truck className="h-4 w-4 text-primary" />
                  </div>
                )}
              </div>

              <DialogFooter className="flex items-center justify-between gap-2 pt-2 border-t border-border/40">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setTargetRescheduleDate(selectedJob.serviceDate);
                    setReschedulingJob(selectedJob);
                  }}
                >
                  <CalendarDays className="h-3.5 w-3.5 mr-1" />
                  <span>Reschedule</span>
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      navigate(`/org/${orgId}/workspace/${workspaceId}/jobs/${selectedJob.id}`);
                      setSelectedJob(null);
                    }}
                  >
                    <span>Job Details</span>
                    <ExternalLink className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Reschedule Dialog ──────────────────────────────────────── */}
      <Dialog open={!!reschedulingJob} onOpenChange={(open) => !open && setReschedulingJob(null)}>
        <DialogContent className="max-w-sm p-4">
          {reschedulingJob && (
            <div className="space-y-3">
              <DialogHeader>
                <DialogTitle className="text-sm font-bold text-foreground">
                  Reschedule Job
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Select a new calendar service date for &quot;{reschedulingJob.title}&quot;
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-1.5 py-1">
                <label className="text-xs font-medium text-foreground">Target Service Date</label>
                <Input
                  type="date"
                  value={targetRescheduleDate}
                  onChange={(e) => setTargetRescheduleDate(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              <DialogFooter className="flex justify-end gap-2 pt-2 border-t border-border/40">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setReschedulingJob(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!targetRescheduleDate || rescheduleMutation.isPending}
                  onClick={handleExecuteReschedule}
                >
                  {rescheduleMutation.isPending && (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  )}
                  <span>Confirm Reschedule</span>
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
